import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import {
  Wallet,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  FileSpreadsheet,
  CreditCard,
  TrendingDown,
  AlertTriangle,
  Building,
  Check,
  X,
  Archive,
  ArchiveRestore
} from "lucide-react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { useTranslation } from "../contexts/I18nContext";

export default function TesoreriaPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [scadenzeApprovate, setScadenzeApprovate] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [paying, setPaying] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    data_pagamento: new Date().toISOString().split('T')[0],
    metodo_pagamento: '',
    note: ''
  });
  const [archiveNote, setArchiveNote] = useState('');
  const [filter, setFilter] = useState('tutti'); // tutti, in_attesa, pagati, archiviati
  const [showArchived, setShowArchived] = useState(false);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr; // Invalid date
      return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const scadenzeRes = await api.get(`${prefix}/tesoreria/scadenze-approvate?includi_archiviati=${showArchived}`);
      setScadenzeApprovate(scadenzeRes.data || []);
    } catch (error) {
      handleApiError(error, t("errors.loadTreasury"));
    } finally {
      setLoading(false);
    }
  }, [showArchived, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle selezione
  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // Seleziona tutti (filtrati)
  const toggleSelectAll = () => {
    const filtered = getFilteredScadenze();
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(s => s.id)));
    }
  };

  // Filtra scadenze
  const getFilteredScadenze = () => {
    return scadenzeApprovate.filter(s => {
      if (filter === 'in_attesa') return s.stato_pagamento !== 'pagato' && !s.archiviato;
      if (filter === 'pagati') return s.stato_pagamento === 'pagato' && !s.archiviato;
      if (filter === 'archiviati') return s.archiviato === true;
      // 'tutti' - mostra non archiviati
      return !s.archiviato;
    });
  };

  // Calcola totali
  const getTotali = () => {
    const nonArchiviati = scadenzeApprovate.filter(s => !s.archiviato);
    const inAttesa = nonArchiviati.filter(s => s.stato_pagamento !== 'pagato');
    const pagati = nonArchiviati.filter(s => s.stato_pagamento === 'pagato');
    const archiviati = scadenzeApprovate.filter(s => s.archiviato === true);
    
    return {
      totale: nonArchiviati.reduce((sum, s) => sum + Math.abs(s.importo || 0), 0),
      inAttesa: inAttesa.reduce((sum, s) => sum + Math.abs(s.importo || 0), 0),
      countInAttesa: inAttesa.length,
      pagati: pagati.reduce((sum, s) => sum + Math.abs(s.importo || 0), 0),
      countPagati: pagati.length,
      archiviati: archiviati.reduce((sum, s) => sum + Math.abs(s.importo || 0), 0),
      countArchiviati: archiviati.length
    };
  };

  // Totale selezionato
  const getSelectedTotal = () => {
    const filtered = getFilteredScadenze();
    return filtered
      .filter(s => selectedIds.has(s.id))
      .reduce((sum, s) => sum + Math.abs(s.importo || 0), 0);
  };

  // Registra pagamento
  const registerPayment = async () => {
    if (selectedIds.size === 0) {
      handleApiError({ message: t("errors.selectAtLeastOneDeadline") });
      return;
    }

    setPaying(true);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/tesoreria/segna-pagata`, {
        ids: Array.from(selectedIds),
        note: paymentForm.note
      });

      showSuccess(`${selectedIds.size} scadenze segnate come pagate`);
      setPaymentDialog(false);
      setSelectedIds(new Set());
      setPaymentForm({
        data_pagamento: new Date().toISOString().split('T')[0],
        metodo_pagamento: '',
        note: ''
      });
      fetchData();
    } catch (error) {
      handleApiError(error, t("errors.operationFailed"));
    } finally {
      setPaying(false);
    }
  };

  // Archivia scadenze pagate (seconda verifica)
  const archiveScadenze = async () => {
    if (selectedIds.size === 0) {
      handleApiError({ message: t("errors.selectAtLeastOnePaidDeadline") });
      return;
    }

    setArchiving(true);
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/tesoreria/archivia`, {
        ids: Array.from(selectedIds),
        note: archiveNote
      });

      showSuccess(`${selectedIds.size} scadenze archiviate`);
      setArchiveDialog(false);
      setSelectedIds(new Set());
      setArchiveNote('');
      fetchData();
    } catch (error) {
      handleApiError(error, t("errors.archiveFailed"));
    } finally {
      setArchiving(false);
    }
  };

  // Annulla approvazione
  const cancelApproval = async (id) => {
    try {
      const prefix = getApiPrefix();
      await api.post(`${prefix}/tesoreria/annulla-approvazione`, { ids: [id] });
      showSuccess(t("success.approvalCancelled"));
      fetchData();
    } catch (error) {
      handleApiError(error, t("errors.errorCancel"));
    }
  };

  // Export Excel
  const exportToExcel = async () => {
    try {
      const prefix = getApiPrefix();
      const dataToExport = selectedIds.size > 0
        ? getFilteredScadenze().filter(s => selectedIds.has(s.id))
        : getFilteredScadenze();

      const response = await api.post(`${prefix}/tesoreria/export`, {
        scadenze: dataToExport
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tesoreria_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess(t("success.exportComplete"));
    } catch (error) {
      handleApiError(error, t("errors.exportFailed"));
    }
  };

  const totali = getTotali();
  const filteredScadenze = getFilteredScadenze();

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  return (
    <div className="space-y-6" data-testid="tesoreria-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-7 w-7 text-blue-600" />
            Tesoreria
          </h1>
          <p className="text-muted-foreground text-sm">Gestione pagamenti scadenze approvate</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button>
          <Button variant="outline" onClick={exportToExcel} disabled={filteredScadenze.length === 0}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />{t('common.exportExcel')}</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Approvate</p>
                <p className="text-2xl font-bold tracking-tight">{scadenzeApprovate.length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">In Attesa Pagamento</p>
                <p className="text-2xl font-bold text-orange-700">{formatCurrency(totali.inAttesa)}</p>
                <p className="text-xs text-orange-500">{totali.countInAttesa} scadenze</p>
              </div>
              <Clock className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Già Pagati</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(totali.pagati)}</p>
                <p className="text-xs text-green-500">{totali.countPagati} scadenze</p>
              </div>
              <Check className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Selezionati</p>
                <p className="text-2xl font-bold tracking-tight">{formatCurrency(getSelectedTotal())}</p>
                <p className="text-xs text-muted-foreground">{selectedIds.size} scadenze</p>
              </div>
              <CreditCard className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filter === 'tutti' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('tutti')}
              >{t('common.all')}</Button>
              <Button
                variant={filter === 'in_attesa' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('in_attesa')}
                className="gap-1"
              >
                <Clock className="h-4 w-4" />
                In Attesa ({totali.countInAttesa})
              </Button>
              <Button
                variant={filter === 'pagati' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('pagati')}
                className="gap-1"
              >
                <Check className="h-4 w-4" />
                Pagati ({totali.countPagati})
              </Button>
              <Button
                variant={filter === 'archiviati' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilter('archiviati');
                  setShowArchived(true);
                }}
                className="gap-1"
              >
                <Archive className="h-4 w-4" />
                Archiviati ({totali.countArchiviati})
              </Button>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              {selectedIds.size > 0 && filter !== 'pagati' && filter !== 'archiviati' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
                  <Button size="sm" onClick={() => setPaymentDialog(true)}>
                    <Check className="h-4 w-4 mr-2" />
                    Segna come Pagate ({selectedIds.size})
                  </Button>
                </>
              )}
              {selectedIds.size > 0 && filter === 'pagati' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
                  <Button size="sm" variant="secondary" onClick={() => setArchiveDialog(true)}>
                    <Archive className="h-4 w-4 mr-2" />
                    Archivia ({selectedIds.size})
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Scadenze Approvate</CardTitle>
          <CardDescription>
            {filteredScadenze.length} scadenze {filter !== 'tutti' ? `(${filter === 'in_attesa' ? 'in attesa' : filter === 'pagati' ? 'pagate - da archiviare' : 'archiviate'})` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredScadenze.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedIds.size === filteredScadenze.length && filteredScadenze.length > 0}
                        onCheckedChange={toggleSelectAll}
                        disabled={filter === 'archiviati'}
                      />
                    </TableHead>
                    <TableHead>{t("common.description")}</TableHead>
                    <TableHead className="w-[120px]">{t('invoices.dueDate')}</TableHead>
                    <TableHead className="w-[120px]">Approvato il</TableHead>
                    <TableHead className="text-right w-[130px]">{t("common.amount")}</TableHead>
                    <TableHead className="w-[120px]">{t("common.status")}</TableHead>
                    <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredScadenze.map((scadenza) => {
                    const overdue = isOverdue(scadenza.data_scadenza);
                    const isPagato = scadenza.stato_pagamento === 'pagato';
                    const isArchiviato = scadenza.archiviato === true;
                    
                    return (
                      <TableRow 
                        key={scadenza.id}
                        className={`${overdue && !isPagato && !isArchiviato ? "bg-red-50" : ""} ${selectedIds.has(scadenza.id) ? "bg-blue-50" : ""} ${isPagato && !isArchiviato ? "bg-green-50/30" : ""} ${isArchiviato ? "bg-gray-50/50" : ""}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(scadenza.id)}
                            onCheckedChange={() => toggleSelection(scadenza.id)}
                            disabled={isArchiviato || (filter === 'tutti' && isPagato)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{scadenza.descrizione}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {scadenza.fornitore || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 ${overdue && !isPagato ? "text-red-600 font-medium" : ""}`}>
                            {overdue && !isPagato && <AlertTriangle className="h-3 w-3" />}
                            {formatDate(scadenza.data_scadenza)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(scadenza.approvato_il)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono font-medium text-red-600">
                            <TrendingDown className="h-3 w-3 inline mr-1" />
                            {formatCurrency(Math.abs(scadenza.importo || 0))}
                          </span>
                        </TableCell>
                        <TableCell>
                          {isArchiviato ? (
                            <Badge className="bg-gray-100 text-gray-600 border-0">
                              <Archive className="h-3 w-3 mr-1" />{t('common.archived')}</Badge>
                          ) : isPagato ? (
                            <Badge className="bg-green-100 text-green-700 border-0">
                              <Check className="h-3 w-3 mr-1" />{t('invoices.paid')}</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              <Clock className="h-3 w-3 mr-1" />{t('common.pending')}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {!isPagato && !isArchiviato && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => cancelApproval(scadenza.id)}
                              title={t("treasury.cancelApproval")}
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                          {isPagato && !isArchiviato && scadenza.data_pagamento && (
                            <span className="text-xs text-muted-foreground">
                              {formatDate(scadenza.data_pagamento)}
                            </span>
                          )}
                          {isArchiviato && scadenza.archiviato_il && (
                            <span className="text-xs text-muted-foreground">
                              {formatDate(scadenza.archiviato_il)}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>{filter === 'archiviati' ? 'Nessuna scadenza archiviata' : 'Nessuna scadenza approvata'}</p>
              {filter !== 'archiviati' && (
                <p className="text-sm mt-2">
                  Vai allo <a href="scadenziario" className="text-blue-600 underline">Scadenziario</a> per approvare le scadenze
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Segna come Pagate */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Segna come Pagate
            </DialogTitle>
            <DialogDescription>
              Segna {selectedIds.size} scadenze come pagate per rimuoverle dalla lista in attesa.
              <br />
              <span className="text-orange-600 text-xs">Nota: questo non chiude la fattura, il pagamento effettivo avviene tramite i movimenti bancari.</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-700">Scadenze selezionate:</span>
                <span className="font-bold text-blue-800">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-blue-700">Importo totale:</span>
                <span className="font-bold text-blue-800">{formatCurrency(getSelectedTotal())}</span>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label>Note (opzionale)</Label>
              <Input
                value={paymentForm.note}
                onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                placeholder={t("common.notes")}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={registerPayment} disabled={paying}>
              {paying ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Archivia */}
      <Dialog open={archiveDialog} onOpenChange={setArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-gray-600" />
              Archivia Scadenze
            </DialogTitle>
            <DialogDescription>
              Archivia {selectedIds.size} scadenze pagate dopo la seconda verifica.
              <br />
              <span className="text-blue-600 text-xs">Le scadenze archiviate verranno rimosse dalla vista principale.</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">Scadenze da archiviare:</span>
                <span className="font-bold text-gray-800">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-700">Importo totale:</span>
                <span className="font-bold text-gray-800">{formatCurrency(getSelectedTotal())}</span>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label>Note archiviazione (opzionale)</Label>
              <Input
                value={archiveNote}
                onChange={(e) => setArchiveNote(e.target.value)}
                placeholder="Es. Verificato da Mario Rossi..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialog(false)}>{t('common.cancel')}</Button>
            <Button variant="secondary" onClick={archiveScadenze} disabled={archiving}>
              {archiving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Archivia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
